import React, { useEffect } from 'react';
import { ReactQueryDevtools } from 'react-query-devtools';
import Routes, { routesURLs } from './Routes';
import useScrollMemory from './hooks/useScrollMemory';
import ModalWrapper from './components/TheModal/ModalWrapper';
import Notification from './components/TheNotification/Notification';
import { deleteCookie, getCookie, getDeviceToken, sessionStore, setCookie } from './services/utils';
import { useCoursesState } from './contexts/courses';
import { GlobalTypes, useGlobalState } from './contexts/global';
import { NotificationStyle } from './services/types';
import { QueryCache, ReactQueryCacheProvider } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { logout, switchUser } from './moduls/auth';
import api from './services/api';
import { exposeQueryCache, hydratePrerenderedQueries } from './seo/prerender';

const queryCache = new QueryCache({
    defaultConfig: {
        queries: {
            retry: 0,
            staleTime: 0,
            cacheTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
        },
    },
});
hydratePrerenderedQueries(queryCache);
exposeQueryCache(queryCache);

function App() {
    useScrollMemory();
    const { state } = useCoursesState();
    const { dispatch } = useGlobalState();
    const location = useLocation();
    const history = useHistory();
    const searchParams = new URLSearchParams(location.search);

    useEffect(() => {
        const hostname = window.location.hostname;

        if (hostname.startsWith('app.')) {
            const newHost = hostname.replace('app.', '');
            const newUrl = `${window.location.protocol}//${newHost}${window.location.pathname}${window.location.search}`;
            window.location.replace(newUrl);
            return;
        }

        window.toggleNotification = (style: NotificationStyle, text: string) =>
            dispatch({ type: GlobalTypes.ToggleNotification, style, text });

        if (history.action === 'REPLACE') {
            dispatch({ type: GlobalTypes.SetPrevRoute, route: location.pathname })
        }

        if (searchParams.get('switch-user')) {
            switchUser(searchParams.get('switch-user') ?? '');
            history.push(routesURLs.dashboard);
        }

        if (!getCookie('device-token')) {
            const newDeviceToken = getDeviceToken();
            if (getCookie('jid')) {
                api.post('/remember-device-token', {device_token: newDeviceToken}).then((res) => {
                    if (!res.data.data.success) {
                        logout();
                    }
                });
            }
        }

        const deviceToken = getCookie('device-token')!;
        deleteCookie('device-token');
        setCookie('device-token', deviceToken, 400);
        if (getCookie('jid')) {
            const jid = getCookie('jid')!;
            deleteCookie('jid');
            setCookie('jid', jid, 400);
        }
        if (getCookie('analytics_user_id')) {
            const analyticsUserId = getCookie('analytics_user_id')!;
            deleteCookie('analytics_user_id');
            setCookie('analytics_user_id', analyticsUserId, 400);
        }

        if (searchParams.get('payment')) {
            let paymentMessage: { text: string; style: NotificationStyle } = {
                text: '',
                style: 'success',
            };
            switch (searchParams.get('payment')) {
                case 'success':
                    paymentMessage.text = 'Platba byla úspěšně provedena.';
                    break;
                case 'cancelled':
                    paymentMessage.text = 'Nastala chyba při provádění platby.';
                    paymentMessage.style = 'error';
                    break;
                case 'pending':
                    paymentMessage.text = 'Čekáme, až se platba provede.';
                    paymentMessage.style = 'info';
                    break;
            }

            dispatch({ type: GlobalTypes.ToggleNotification, style: paymentMessage.style, text: paymentMessage.text });
        }

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        sessionStore.set('onsite_courses', state.savedPersonalCourses);
        sessionStore.set('online_courses', state.savedOnlineCourses);
        sessionStore.set('online_mock_exam_courses', state.savedOnlineMockExamCourses);
        sessionStore.set('onsite-tests', state.savedPersonalTests);
    }, [state.savedPersonalCourses, state.savedOnlineCourses, state.savedOnlineMockExamCourses, state.savedPersonalTests]);

    return (
        <div className="App">
            <ReactQueryCacheProvider queryCache={queryCache}>
                <Notification />
                <Routes />
                <ModalWrapper />
            </ReactQueryCacheProvider>
            <ReactQueryDevtools initialIsOpen={false} />
        </div>
    );
}

export default App;
